import { useEffect, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { useAppStore } from "@/stores/app.store";
import { relativeTimeParts } from "@/utils/relative-time";

const FEED_ROWS = 4;
const TICK_MS = 60 * 1000;

/** One row of a latest-wins feed, whatever campaign type produced it. */
export interface LatestWinRow {
  key: string;
  /** The platform's opaque player hash, compared with the reader's own to render "You";
   *  `null` when the source does not know who won (the row then reads as a plain fact). */
  player: string | null;
  /** The row's lead text — the platform's already-masked username ("ale**"), or, when no name is
   *  known, the amount. */
  name: string;
  /** Optional second cell after the name — e.g. which jackpot it was. Muted, never truncates the
   *  name to make room for itself. */
  detail?: string;
  /** ISO timestamp of the win; rendered as a relative time that ticks once a minute. */
  wonAt: string;
}

/**
 * The "Latest wins" sidebar card, shared by every campaign type that has winners to show: a
 * section label, an optional pace line the owning type composes (it gets the ticking `now`, so
 * "N winners in the last hour" and a countdown stay current), then up to four newest rows with an
 * initial, the masked name (or "You" for the reader's own win, the way the tournament leaderboard
 * does it), an optional detail and a relative time.
 *
 * **Renders nothing until the first win: there is no empty state, by decision** — the rule that
 * came with the Sprint Mission card and holds for jackpots ("only if there is a winner").
 * Finished runs keep the rows; the pace line is the caller's to drop.
 *
 * The relative-time strings reuse the `sprintMission.*` keys on purpose: they are already
 * translated in every locale and their meaning is not type-specific; a new key set would only
 * re-translate the same four phrases.
 */
export const LatestWinsCard = ({
  title,
  rows,
  renderPace,
  finished = false,
  density = "default",
  className,
  testId,
}: {
  title: string;
  rows: LatestWinRow[];
  /** Composed by the owning type; receives the ticking clock. Return `null` for no pace line. */
  renderPace?: (now: number) => ReactNode;
  finished?: boolean;
  /** `dock`: the overlay's narrow column — the detail (a jackpot's win amount) goes under the
   *  name on its own line instead of a capped share of one row, so the amount stays whole. */
  density?: "default" | "dock";
  className?: string;
  testId?: string;
}) => {
  const { t } = useTranslation();
  // The feed is a shared stream, so the reader's own row is found by hash — the same comparison
  // the tournament leaderboard makes — and shown as "You" rather than their masked name.
  const playerHash = useAppStore(
    (state) =>
      state.playerHash ?? (state.authData?.player_hash as string) ?? null
  );

  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (rows.length === 0) return;
    const timer = setInterval(() => setNow(Date.now()), TICK_MS);
    return () => clearInterval(timer);
  }, [rows.length]);

  if (rows.length === 0) return null;

  const relative = (wonAt: string): string => {
    const { key, count } = relativeTimeParts(wonAt, now);
    switch (key) {
      case "justNow":
        return t("sprintMission.justNow", "just now");
      case "minutesAgo":
        return t("sprintMission.minutesAgo", "{{count}} min ago", { count });
      case "hoursAgo":
        return t("sprintMission.hoursAgo", "{{count}} h ago", { count });
      default:
        return t("sprintMission.daysAgo", "{{count}} d ago", { count });
    }
  };

  const pace = renderPace ? renderPace(now) : null;

  return (
    <Card
      data-testid={testId}
      title={title}
      className={cn("flex flex-col gap-3", className)}
      titleRowClassName="mb-0"
    >
      {pace}

      <ul className="flex w-full flex-col gap-2">
        {rows.slice(0, FEED_ROWS).map((row, index) => {
          const isYou = playerHash !== null && row.player === playerHash;
          const stacked = density === "dock";
          const name = (
            <span
              className={cn(
                "min-w-0 truncate font-heading text-[12px] leading-4 font-semibold lg:text-[13px] ls:text-[10px]",
                !stacked && "flex-1",
                isYou ? "text-primary" : "text-foreground"
              )}
            >
              {isYou ? t("tournament.you", "You") : row.name}
            </span>
          );
          const detail = row.detail && (
            <span
              className={cn(
                "truncate font-body text-[12px] leading-4 text-muted-foreground ls:text-[10px]",
                !stacked && "max-w-[45%] shrink-0"
              )}
            >
              {row.detail}
            </span>
          );
          return (
            <li key={row.key} className="flex w-full items-center gap-3">
              <span
                aria-hidden
                className="flex size-6 shrink-0 items-center justify-center rounded-full bg-background-card-surface font-heading text-[11px] font-bold text-foreground-secondary uppercase ls:size-5 ls:text-[9px]"
              >
                {row.name.charAt(0)}
              </span>
              {stacked ? (
                <span className="flex min-w-0 flex-1 flex-col">
                  {name}
                  {detail}
                </span>
              ) : (
                <>
                  {name}
                  {detail}
                </>
              )}
              <span
                className={cn(
                  "shrink-0 font-body text-[12px] leading-4 tabular-nums ls:text-[10px]",
                  index === 0 && !finished
                    ? "font-semibold text-active"
                    : "text-muted-foreground"
                )}
              >
                {relative(row.wonAt)}
              </span>
            </li>
          );
        })}
      </ul>
    </Card>
  );
};
